import {
  AttachMoney,
  Delete,
  Edit,
  MoreVert,
  Person,
  Visibility
} from '@mui/icons-material';
import {
  Box,
  Card,
  CardContent,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Typography
} from '@mui/material';
import React from 'react';
import { Draggable } from 'react-beautiful-dnd';
import ReactDOM from 'react-dom';

const DealCard = ({
  deal,
  index,
  onEdit,
  onDelete,
  onView,
  clients = [],
  products = [],
  leads = []
}) => {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);

  const handleMenuClick = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEdit = () => {
    handleMenuClose();
    if (onEdit) onEdit(deal);
  };

  const handleDelete = () => {
    handleMenuClose();
    if (onDelete) onDelete(deal);
  };

  const handleView = () => {
    handleMenuClose();
    if (onView) onView(deal);
  };

  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    return client ? client.name : 'Неизвестный клиент';
  };

  const getLeadName = (leadId) => {
    const lead = leads.find(l => l.id === leadId);
    return lead ? lead.name : 'Неизвестный лид';
  };

  const getContactName = () => {
    // Приоритет: сначала лид, затем клиент
    if (deal.leadId) {
      return getLeadName(deal.leadId);
    } else if (deal.client_id) {
      return getClientName(deal.client_id);
    }
    return 'Контакт не указан';
  };

  const getProductName = (productId) => {
    const product = products.find(p => p.id === productId);
    return product ? product.name : 'Неизвестный продукт';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: 'RUB'
    }).format(amount || 0);
  };

  const getStatusColor = (status) => {
    const statusMap = {
      'new': '#3B82F6',
      'in_progress': '#F59E0B',
      'completed': '#10B981',
      'canceled': '#EF4444'
    };
    return statusMap[status] || '#6B7280';
  };

  const getPortalContainer = React.useCallback(() => {
    let node = document.getElementById('dnd-portal');
    if (!node) {
      node = document.createElement('div');
      node.id = 'dnd-portal';
      document.body.appendChild(node);
    }
    return node;
  }, []);

  return (
    <Draggable draggableId={deal.id.toString()} index={index}>
      {(provided, snapshot) => {
        const combinedStyle = { ...provided.draggableProps.style };
        const cardEl = (
          <Card
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            style={combinedStyle}
            sx={{
              mb: 2,
              cursor: snapshot.isDragging ? 'grabbing' : 'grab',
              opacity: snapshot.isDragging ? 0.9 : 1,
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: '12px',
              boxShadow: snapshot.isDragging
                ? '0 8px 40px rgba(0, 0, 0, 0.3)'
                : '0 4px 20px rgba(0, 0, 0, 0.1)',
              transition: snapshot.isDragging ? 'none' : 'all 0.2s ease',
              zIndex: snapshot.isDragging ? 2000 : 'auto',
              position: 'relative',
              '&:hover': !snapshot.isDragging ? {
                boxShadow: '0 8px 30px rgba(0, 0, 0, 0.15)'
              } : {}
            }}
          >
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Typography
                  variant="h6"
                  component="h3"
                  sx={{
                    fontWeight: 'bold',
                    fontSize: '1rem',
                    color: 'white',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: 'calc(100% - 40px)'
                  }}
                >
                  {deal.title}
                </Typography>
                <IconButton
                  size="small"
                  onClick={handleMenuClick}
                  sx={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    '&:hover': { color: 'white' }
                  }}
                >
                  <MoreVert fontSize="small" />
                </IconButton>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Person sx={{ fontSize: 16, color: 'rgba(255, 255, 255, 0.7)', mr: 1 }} />
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'rgba(255, 255, 255, 0.8)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {getContactName()}
                  </Typography>
                </Box>

                <Typography
                  variant="body2"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.6)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    mb: 1
                  }}
                >
                  {getProductName(deal.product_id)}
                  {deal.quantity && ` × ${deal.quantity}`}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <AttachMoney sx={{ fontSize: 16, color: '#10B981', mr: 0.5 }} />
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 'bold',
                      color: '#10B981'
                    }}
                  >
                    {formatCurrency(deal.amount)}
                  </Typography>
                </Box>

                {deal.probability !== undefined && (
                  <Chip
                    label={`${deal.probability}%`}
                    size="small"
                    sx={{
                      background: 'rgba(255, 255, 255, 0.15)',
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: '0.7rem'
                    }}
                  />
                )}
              </Box>

              {/* Индикатор статуса */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '4px',
                  height: '100%',
                  backgroundColor: getStatusColor(deal.status),
                  borderRadius: '12px 0 0 12px'
                }}
              />
            </CardContent>

            {/* Контекстное меню */}
            <Menu
              anchorEl={anchorEl}
              open={open}
              onClose={handleMenuClose}
              PaperProps={{
                sx: {
                  background: 'rgba(30, 30, 30, 0.9)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                }
              }}
            >
              <MenuItem onClick={handleView} sx={{ color: 'white' }}>
                <Visibility sx={{ mr: 1, fontSize: 18 }} />
                Просмотр
              </MenuItem>
              <MenuItem onClick={handleEdit} sx={{ color: 'white' }}>
                <Edit sx={{ mr: 1, fontSize: 18 }} />
                Редактировать
              </MenuItem>
              <MenuItem onClick={handleDelete} sx={{ color: '#EF4444' }}>
                <Delete sx={{ mr: 1, fontSize: 18 }} />
                Удалить
              </MenuItem>
            </Menu>
          </Card>
        );
        return snapshot.isDragging
          ? ReactDOM.createPortal(cardEl, getPortalContainer())
          : cardEl;
      }}
    </Draggable>
  );
};

export default DealCard;
